/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/joy/Button';
import Divider from '@mui/joy/Divider';
import DialogTitle from '@mui/joy/DialogTitle';
import DialogContent from '@mui/joy/DialogContent';
import DialogActions from '@mui/joy/DialogActions';
import FormHelperText from '@mui/joy/FormHelperText';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import Typography from '@mui/joy/Typography';
import ContentCopy from '@mui/icons-material/ContentCopy';
import CustomTextField from '../../../main/widgets/CustomTextField';
import { duplicatePricingRule } from '../../../main/API/pricingRule';
import { pricingRuleApiValidationFeedback } from './pricingRuleFormPayload';

function suggestedDuplicateCode(sourceCode) {
  return sourceCode ? `${sourceCode}_copy` : '';
}

function suggestedDuplicateName(sourceName) {
  return sourceName ? `${sourceName} (Copy)` : '';
}

export default function DuplicatePricingRuleDialog({ open, onClose, sourceRule, onSuccess }) {
  const { t } = useTranslation('ui');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveError, setSaveError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !sourceRule) return;
    setCode(suggestedDuplicateCode(sourceRule.code || ''));
    setName(suggestedDuplicateName(sourceRule.name || ''));
    setFieldErrors({});
    setSaveError('');
  }, [open, sourceRule]);

  const validateClient = () => {
    const errors = {};
    const trimmedCode = code.trim();
    if (!trimmedCode) {
      errors.code = t('pricingRules.duplicateCodeRequired');
    } else if (sourceRule?.code && trimmedCode === sourceRule.code) {
      errors.code = t('pricingRules.duplicateCodeSameAsSource');
    }
    return errors;
  };

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!sourceRule?.id) return;
    setSaveError('');
    const clientErrors = validateClient();
    setFieldErrors(clientErrors);
    if (Object.keys(clientErrors).length) return;

    const payload = { code: code.trim() };
    const trimmedName = name.trim();
    if (trimmedName) payload.name = trimmedName;

    setLoading(true);
    try {
      const response = await duplicatePricingRule(sourceRule.id, payload);
      if (response.status === 201) {
        onSuccess(response.data);
        onClose();
      }
    } catch (err) {
      const { fieldErrors: apiFieldErrors, saveError: apiBanner } = pricingRuleApiValidationFeedback(
        err,
        t
      );
      setFieldErrors(apiFieldErrors);
      setSaveError(apiBanner);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog variant="outlined" sx={{ maxWidth: 480, width: '100%' }}>
        <DialogTitle>
          <ContentCopy />
          {t('pricingRules.duplicateDialogTitle')}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography level="body-sm" sx={{ mb: 2 }}>
            {t('pricingRules.duplicateDialogBody')}
          </Typography>
          <CustomTextField
            required
            label={t('pricingRules.fieldCode')}
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              if (fieldErrors.code) {
                setFieldErrors((prev) => {
                  const next = { ...prev };
                  delete next.code;
                  return next;
                });
              }
            }}
            fullWidth
            sx={{ mb: 2 }}
          />
          {fieldErrors.code ? (
            <FormHelperText sx={{ color: 'danger.500', mt: -1.5, mb: 2 }}>
              {fieldErrors.code}
            </FormHelperText>
          ) : null}
          <CustomTextField
            label={t('pricingRules.fieldName')}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (fieldErrors.name) {
                setFieldErrors((prev) => {
                  const next = { ...prev };
                  delete next.name;
                  return next;
                });
              }
            }}
            fullWidth
          />
          {fieldErrors.name ? (
            <FormHelperText sx={{ color: 'danger.500', mt: 0.5 }}>
              {fieldErrors.name}
            </FormHelperText>
          ) : null}
          {saveError ? (
            <Typography color="danger" level="body-sm" sx={{ mt: 2 }}>
              {saveError}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button loading={loading} variant="solid" color="primary" onClick={handleSubmit}>
            {t('pricingRules.buttonDuplicate')}
          </Button>
          <Button variant="plain" color="neutral" onClick={handleClose} disabled={loading}>
            {t('actions.cancel')}
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}
